import { Database } from "firebase/database"
import {
  applyMiddleware,
  createStore,
  compose,
  combineReducers,
  AnyAction,
} from "redux"
import thunk, { ThunkAction, ThunkDispatch } from "redux-thunk"
import { persistStore, persistReducer, createTransform } from "redux-persist"
import storage from "redux-persist/lib/storage"
import storageSession from "redux-persist/lib/storage/session"
import { useSelector } from "react-redux"

import { authMiddleware } from "./authMiddleware"
import { encode, decode } from "../utils/transforms"
import firebaseReducer from "../reducers/firebase"
import booking from "../reducers/booking"
import admin from "../reducers/admin"
import instructor from "../reducers/instructor"
import globalUi from "../reducers/globalUi"
import user from "../reducers/user"
import location from "../reducers/location"
import driversEd from "../reducers/driversEd"
import prep from "../reducers/prep"
import courses from "../reducers/courses"
import search from "../reducers/search"
import cart from "../reducers/cart"
import { listenUserUpdateRedux } from "../firebase"

const persistConfig = {
  key: "root",
  storage,
  transforms: [createTransform(encode, decode)], // for handling dates
  blacklist: ["courses"],
}

const locationConfig = {
  key: "location",
  storage: storageSession,
  transforms: [createTransform(encode, decode)],
}

const bookingConfig = {
  key: "booking",
  storage: storageSession,
  transforms: [createTransform(encode, decode)],
}

const rootReducer = combineReducers({
  firebase: firebaseReducer,
  instructor,
  admin,
  globalUi,
  user,
  driversEd,
  prep,
  courses,
  search,
  location: persistReducer(locationConfig, location),
  booking: persistReducer(bookingConfig, booking),
  cart,
})

const persistedReducer = persistReducer(persistConfig, rootReducer)

const configureStore = () => {
  const store = createStore(
    persistedReducer,
    {},
    compose(
      applyMiddleware(thunk),
      applyMiddleware(authMiddleware),
      // eslint-disable-next-line
      typeof window === "object" &&
        // eslint-disable-next-line
        typeof (window as any).__REDUX_DEVTOOLS_EXTENSION__ !== "undefined"
        ? // eslint-disable-next-line
          (window as any).__REDUX_DEVTOOLS_EXTENSION__()
        : (f: any) => f,
    ),
  )

  if ((module as any).hot) {
    // Enable Webpack hot module replacement for reducers
    ;(module as any).hot.accept("./configureStore", () => {
      // eslint-disable-next-line
      const nextRootReducer = require("./configureStore")
      store.replaceReducer(nextRootReducer)
    })
  }

  const persistor = persistStore(store)

  listenUserUpdateRedux(store.dispatch, store.getState)

  return { store, persistor }
}

export type RootState = ReturnType<typeof rootReducer>

export const useSelectorTyped = <T>(fn: (state: RootState) => T) =>
  useSelector(fn)

export type AppThunkAction<T = void> = ThunkAction<
  T,
  RootState,
  () => Database,
  AnyAction
>

export type AppThunkDispatch = ThunkDispatch<
  RootState,
  () => Database,
  AnyAction
>

export default configureStore
